// Package templates: the reusable thing a package is sold *from* — and the
// renderings of the unit they are measured in.
//
// The two halves sit together because a package is a block of *time* (#181),
// and `2 hr 30 min` on the Templates list, `4h 30m` on the student rail and
// the sentence the attendance drawer shows when a lesson spends some are one
// vocabulary.
//
// ── What MyMusicStaff does (#181) ──────────────────────────────────────────
//
// Packages follow MMS — templates and hours. A make-up credit is **time** too
// (#352), so the two share one unit.
//
// `Packages` is one page with two tabs, `Package Balances` and `Templates`.
// `Templates → + Package Template` opens a drawer titled `Add Package
// Template` / `Edit Package Template` carrying, in order:
//
//   Package Template Name           text      required — "This field is required"
//   Description                     textarea  optional, hinted `Optional`
//   Duration                        two numbers, `hours` + `minutes`
//                                             required — "Please enter an amount
//                                             greater than 0"
//   Price                           $ number  required — "This field is required"
//   Status                          radio     Active / Inactive, defaults Active
//   Package Expiry                  toggle    off by default
//   Days to Expire After Start Date number    required **when the toggle is on**
//
// The list columns are `Name | Duration | Expiration | Description | Price`
// with a `⋮` offering `Edit` and `Delete`. Duration renders `2 hr 30 min`;
// expiry renders `30 days` or `No Expiry`; a blank description renders `-`.
//
// **The form refuses almost nothing.** Duration *normalises* instead of
// refusing: typing `90` into `minutes` becomes `1 hours` / `30 minutes` on
// blur, and `0 h 0 m` is the one refusal. Price strips a typed minus
// **silently** — `-50` leaves `50` — as MMS's expense `Amount` field does
// (#173, #166); decimals are kept and it formats to `$ 120.00` on blur.
//
// This form copies the stripping. `src/domain/packagePrice.js` carries a
// refusal (#173) for the per-sale `Customize Charge` override, where a teacher
// is typing into a field that already holds a number; see its header.
//
// ── The template is a form-filler, not a foreign key ───────────────────────
//
// In MMS, renaming, repricing, re-timing and then *deleting* a template moves
// nothing on a completed sale: the ledger charge keeps its words and the
// balances are untouched. So name, price and duration are **copied onto the
// sale** and nothing joins back.
//
// Delete is soft, as MMS's confirm dialog says: *"If this package template has
// been used, it will be marked as 'Inactive'."* — which is what the `Status`
// radio is for. MMS then drops the row from the `Templates` list, and no
// filter in MMS is known to show inactive templates. [`isListedTemplate`]
// below is where ours is decided.
//
// ── Adapters ───────────────────────────────────────────────────────────────
//
// The rules below — the durations, the form's refusals, the minus stripped,
// the sale's ledger line, the listed templates, the Edit form and the two
// documents — are `core/src/package_sale.rs`, so a phone's template drawer and
// list behave as this one does; `core/src/family_money_vectors.json` holds
// the recorded answers. What stays is the coercion and the four refusal
// sentences the tests read at module scope (pinned to the core's by
// test/domain/familyMoneyCore.test.js).

import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';

const text = (v) => (v == null ? null : String(v));
const num = (v) => (v == null ? null : Number(v));

// ── Duration ───────────────────────────────────────────────────────────────

/** The two boxes as one stored figure. */
export const totalMinutes = (hours, minutes) => coreSync().packageTemplateMinutes(text(hours), text(minutes));

/**
 * MMS's blur: `90` minutes becomes `1 hours` / `30 minutes`.
 *
 * Normalising rather than refusing is MMS's behaviour — the minutes box
 * accepts any number and carries the overflow into hours — so this is what the
 * form calls on blur rather than a validator.
 */
export function normaliseDuration(hours, minutes) {
  const d = coreSync().normalisePackageDuration(text(hours), text(minutes));
  return { hours: d.hours, minutes: d.minutes };
}

/**
 * `2 hr 30 min`, which is the Templates list's rendering.
 *
 * Zero on either side is dropped: MMS's list shows `5 hr` and `30 min`, never
 * `5 hr 0 min`. MMS's form will not save a total of zero, so `0 min` is ours.
 */
export const formatDuration = (minutes) => coreSync().formatPackageDuration(num(minutes));

/**
 * `4h 30m`, which is the **student rail's** rendering of the same figure.
 *
 * Two renderings because MMS has two: `Package Time — Upcoming Lessons: 4h 30m,
 * Available: 0h 30m` on the rail against `2 hr 30 min` on the Templates list.
 * The rail always shows both halves (`0h 00m`), so this one does not drop a
 * zero the way [`formatDuration`] does.
 *
 * A **negative** is rendered with a leading minus rather than clamped, because
 * the forecast's balance can legitimately go short: a lesson longer than the
 * remainder still draws (see `should_consume_package` in the core). A figure
 * that clamped here would tell a teacher the package came out even.
 */
export function formatClock(minutes) {
  return coreSync().formatClock(minutes == null ? null : Number(minutes));
}

/** `30 days` / `No Expiry`, the Templates list's `Expiration` column. */
export const formatExpiry = (days) => coreSync().formatPackageExpiry(num(days));

// ── The form ───────────────────────────────────────────────────────────────

export const TEMPLATE_NAME_REQUIRED = 'This field is required';
export const TEMPLATE_DURATION_REQUIRED = 'Please enter an amount greater than 0';
export const TEMPLATE_PRICE_REQUIRED = 'This field is required';
export const TEMPLATE_EXPIRY_DAYS_REQUIRED = 'This field is required';

/** A blank template form, with MMS's defaults: Active, expiry off. */
export const BLANK_TEMPLATE = () => coreSync().blankPackageTemplate();

// A form as the core's `PackageTemplateForm`: each box as its text.
function formIn(form) {
  return {
    name: String(form?.name ?? ''),
    description: String(form?.description ?? ''),
    hours: String(form?.hours ?? ''),
    minutes: String(form?.minutes ?? ''),
    price: String(form?.price ?? ''),
    status: String(form?.status ?? ''),
    expires: Boolean(form?.expires),
    expiryDays: String(form?.expiryDays ?? ''),
  };
}

/**
 * Field-keyed errors, empty when the form would save.
 *
 * The three sentences are MMS's own. They are returned per field, as the
 * drawer shows them under the box, because a disabled Save button says nothing
 * about which field is wrong (#164, #171).
 *
 * Required, and that is all this asks of the price: the *magnitude* rule is
 * `money.amountError`'s, which `templateFormErrors` adds; a minus is stripped
 * at the keystroke by `stripMinus` below rather than refused here, which is
 * what MMS does.
 */
export function templateErrors(form) {
  const e = coreSync().packageTemplateFormErrors(formIn(form), 'USD');
  const errors = {};
  if (e.nameCode) errors.name = e.nameMessage;
  if (e.durationCode) errors.duration = e.durationMessage;
  if (e.priceCode && e.priceCode !== 'amount_too_large') errors.price = e.priceMessage;
  if (e.expiryDaysCode) errors.expiryDays = e.expiryDaysMessage;
  return errors;
}

/**
 * A typed minus dropped, the way MMS's Price box drops it at the keystroke.
 *
 * Not a clamp of the stored value — the character never survives the field, so
 * `-50` reads `50` while the teacher is still typing. The currency's decimal
 * places (#308) are applied too, so the caller has one call rather than two to
 * remember.
 */
export const stripMinus = (value, currency = 'USD') => coreSync().stripMinus(String(value ?? ''), currency);

// ── The sale ───────────────────────────────────────────────────────────────

/**
 * What the ledger row for a package sale says.
 *
 * MMS's: `Lesson - <template name> - <teacher name>`, on a row dated the
 * **package start date** and typed `Package`. Hyphens rather than em dashes,
 * and no colon, so the two ledgers compare as strings.
 *
 * Both names are the values **at the moment of sale**; the template can be
 * renamed or deleted afterwards and this row does not move (see the header).
 */
export const packageChargeDescription = (templateName, teacherName) => (
  coreSync().packageChargeDescription(text(templateName), text(teacherName))
);

// A stored template as the core's `StoredPackageTemplate`.
function storedTemplate(t) {
  return {
    name: text(t?.name),
    description: text(t?.description),
    minutes: num(t?.minutes),
    priceMinor: num(t?.priceMinor),
    status: text(t?.status),
    expiryDays: num(t?.expiryDays),
    deleted: Boolean(t?.deleted),
  };
}

/**
 * Whether a template belongs in the picker and the Templates list.
 *
 * Inactive templates are out of both. MMS drops a soft-deleted template from
 * its Templates list and no filter is known to show it again; this hides it
 * too. The core's `is_listed` is the one place to change.
 */
export function isListedTemplate(t) {
  return Boolean(t) && coreSync().listedPackageTemplates([storedTemplate(t)]).length === 1;
}

// ── Spending it ────────────────────────────────────────────────────────────

/**
 * What the attendance drawer says when a lesson will draw on package time.
 *
 * **Ours.** MyMusicStaff shows the bare word `Package` under the attendee's
 * name in its event popover, where an ordinary lesson shows a dollar amount.
 *
 * It says what this lesson takes **and** what is left (#181), and it does not
 * promise the balance covers it:
 * `should_consume_package` draws on *any* remaining time, so a lesson longer
 * than the remainder is still covered and leaves the package short.
 */
export function packageDrawSentence(lessonMinutes, remainingMinutes) {
  const n = (v) => (v == null ? null : Number(v));
  return coreSync().packageDrawSentence(n(lessonMinutes), n(remainingMinutes));
}

// ── The managed list ───────────────────────────────────────────────────────

/**
 * The templates the picker and the Templates list show, by name, as every
 * other managed list here is sorted. MMS's order is not known.
 */
export function listedTemplates(stored) {
  return [...coreSync().listedPackageTemplates(stored.map(storedTemplate))].map((i) => stored[i]);
}

/** A picker option: the name, the duration and the price written out. */
export const templateOptionLabel = (t, priceText) => coreSync().packageTemplateOptionLabel(
  String(t.name), num(t.minutes), String(priceText),
);

/**
 * The Edit form opened on a stored template: the duration split into its two
 * boxes, the price as a decimal (a number, as the box holds it), the
 * expiry toggle on when days are set.
 */
export function templateFormOf(row, currency) {
  const f = coreSync().packageTemplateFormOf(storedTemplate(row), currency);
  return { ...f, price: Number(f.price) };
}

/**
 * What the drawer shows under each box — `templateErrors`, with the price's
 * magnitude under Price once something is typed — and whether Save refuses:
 * it does whenever any of them is set.
 */
export function templateFormErrors(form, currency) {
  const e = coreSync().packageTemplateFormErrors(formIn(form), currency);
  return {
    name: e.nameMessage ?? null,
    duration: e.durationMessage ?? null,
    price: e.priceMessage ?? null,
    expiryDays: e.expiryDaysMessage ?? null,
    refused: e.refused,
  };
}

/** The form read into what `saveTemplate` takes: the price to minor units. */
export function templateSaveInput(form, currency) {
  return {
    name: form.name,
    description: form.description,
    hours: form.hours,
    minutes: form.minutes,
    priceMinor: coreSync().packageTemplateSaveInput(formIn(form), currency).priceMinor,
    status: form.status,
    expires: form.expires,
    expiryDays: form.expiryDays,
  };
}

/**
 * The document Save writes, merged over the template's id — or null when the
 * duration is not positive, which writes nothing. Create and edit alike.
 */
export function packageTemplateDocument({
  name, description, hours, minutes, priceMinor, status, expires, expiryDays,
}, serverTimestamp) {
  const rows = coreSync().packageTemplateDocument({
    name: text(name),
    description: text(description),
    hours: text(hours),
    minutes: text(minutes),
    priceMinor: num(priceMinor),
    status: text(status),
    expires: Boolean(expires),
    expiryDays: text(expiryDays),
  });
  return rows ? documentFromRows(rows, serverTimestamp) : null;
}

/** Delete, which is soft: both fields, merged. */
export const templateRemovalDocument = (serverTimestamp) => documentFromRows(
  coreSync().packageTemplateRemovalDocument(), serverTimestamp,
);
